// src/components/organisation/OrganisationSettings.jsx
import React, { useState, useEffect } from 'react';
import { 
  Building, Users, Settings, Globe, Layout, Code, Zap, Mail, Calendar,
  Shield, MessageCircle, CreditCard, Eye, Palette 
} from 'lucide-react';
import { SettingsModal } from '../settings/shared/SettingsModal';
import { TabNavigation } from '../settings/shared/TabNavigation';

// Import ALL shared settings components
import { BasicInfoTab } from '../settings/shared/BasicInfoTab';
import { ManagementSection } from '../settings/shared/ManagementSection';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { LayoutDisplaySection } from '../settings/shared/LayoutSection';
import { InteractionsSection } from '../settings/shared/InteractionsSection';
import { PaymentsSection } from '../settings/shared/PaymentsSection';
import { PrivacySection } from '../settings/shared/PrivacySection';
import { EmailSettingsSection } from '../settings/shared/EmailSettingsSection';
import { IntegrationsSection } from '../settings/shared/IntegrationsSection';
import { SEOSection } from '../settings/shared/SEOSection';
import { AdvancedSection } from '../settings/shared/AdvancedSection';
import { BookingSection } from '../settings/shared/BookingSection';
import { HeaderSettings } from '../settings/shared/HeaderSettings';

import { organisationService } from '../../services/organisationService';
import { getDefaultBookingConfig } from '../../services/bookingConfigurationService';
import { firestoreService } from '../../services/firestoreService';

export const OrganisationSettings = ({ book, user, bookId, onUpdate, onClose }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploadStates, setUploadStates] = useState({});
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState(() => {
    const defaultBookingConfig = getDefaultBookingConfig(book);
    
    return {
    // Basic info
    botName: '',
    description: '',
    organisationType: 'business',
    tagline: '',
    establishedYear: '',
    logoUrl: '',
    coverImageUrl: '',
    
    // Organisation-specific
    organisationSettings: {
      sector: '',
      membershipType: 'open', // open, invitation, application
      enableMemberDirectory: true,
      enableEvents: true,
      enableNews: true,
      enableDonations: false,
      enableVolunteering: false,
      publicContact: true
    },

    // Management features (universal)
    managementFeatures: {},

    // Design & styling
    primaryColor: '#3b82f6',
    secondaryColor: '#10b981',
    fontFamily: 'Inter',
    headerStyle: 'modern',
    
    // Layout & display
    layoutStyle: 'modern',
    enableSections: {
      hero: true,
      about: true,
      leadership: true,
      services: true,
      events: true,
      news: true,
      contact: true
    },
    sectionOrder: ['hero', 'about', 'leadership', 'services', 'events', 'news', 'contact'],

    enableBooking: book?.enableBooking || false,
    bookingOrgName: book?.bookingOrgName || book?.botName || '',
    bookingTimezone: book?.bookingTimezone || 'Africa/Johannesburg',
    
    // Booking Services
    bookingServices: {
      appointment: book?.bookingServices?.appointment || false,
      consultation: book?.bookingServices?.consultation || false,
      class: book?.bookingServices?.class || false,
      facility: book?.bookingServices?.facility || false,
      event: book?.bookingServices?.event || false,
      ...book?.bookingServices
    },
    
    // Business Hours
    businessHours: {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: false, start: '09:00', end: '17:00' },
      sunday: { enabled: false, start: '09:00', end: '17:00' },
      ...book?.businessHours
    },
    
    // Payment Configuration
    bookingPaymentMethod: book?.bookingPaymentMethod || 'free',
    stripePublishableKey: book?.stripePublishableKey || '',
    stripeSecretKey: book?.stripeSecretKey || '',
    payfastMerchantId: book?.payfastMerchantId || '',
    payfastMerchantKey: book?.payfastMerchantKey || '',
    
    // Advanced Booking Settings
    defaultBookingDuration: book?.defaultBookingDuration || '60',
    bookingBuffer: book?.bookingBuffer || '15',
    requireBookingApproval: book?.requireBookingApproval || false,
    sendBookingNotifications: book?.sendBookingNotifications !== false, // Default true
    allowRescheduling: book?.allowRescheduling !== false, // Default true
    enableBookingPortal: book?.enableBookingPortal || false,
    
    // Booking Policies
    cancellationPolicy: book?.cancellationPolicy || '24-hour notice required for cancellations',
    bookingTerms: book?.bookingTerms || '',
    maxAdvanceBookingDays: book?.maxAdvanceBookingDays || 30,
    minAdvanceBookingHours: book?.minAdvanceBookingHours || 2,
    // Interactions
    enableContactForm: true,
    enableNewsletter: true,
    enableSocialSharing: true,
    enableComments: false,
    requireContactName: true,
    requireContactEmail: true,
    requireContactPhone: false,
    autoResponse: '',
    notificationEmail: '',

    // Payments (for donations, memberships, events)
    enablePayments: false,
    paymentProcessor: '',
    currency: 'ZAR',
    enableDonations: false,
    enableMemberships: false,
    enableEventPayments: false,

    // Privacy & access
    isPublic: true,
    requireAuth: false,
    allowedUsers: [],
    enableAnalytics: true,
    cookieConsent: true,
    dataRetention: '2years',

    // SEO & Domain
    customDomain: '',
    customPath: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    ogImage: '',
    enableStructuredData: true,
    allowIndexing: true,

    // Integrations
    integrations: {
      googleAnalytics: '',
      facebookPixel: '',
      mailchimp: '',
      stripe: '',
      payfast: '',
      zoom: '',
      slack: ''
    },

    // SEO & domain
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    customDomain: '',
    customPath: '',
    enableSitemap: true,
    enableRobotsTxt: true,

    emailNotifications: book?.settings?.emailNotifications || false,
    welcomeEmailsEnabled: book?.settings?.welcomeEmails || false,
    booking_confirmationEnabled: book?.settings?.bookingConfirmations || false,
    reminderEnabled: book?.settings?.reminderEmails || false,
    completion_certificateEnabled: book?.settings?.completionEmails || false,
    interaction_summaryEnabled: book?.settings?.interactionSummaries || false,
    welcomeMessage: book?.settings?.welcomeMessage || '',
    completionMessage: book?.settings?.completionMessage || '',
    userNotificationFreq: book?.settings?.userNotificationFreq || 'weekly',
    adminNotificationFreq: book?.settings?.adminNotificationFreq || 'daily',

    // Element Visibility
    showLogo: true,
    showTitle: true,
    showDescription: false,
    showSearch: true,
    showShareButton: true,
    showAccessLevel: true,
    showFilterControls: true,
    showGallerySelector: true,
    showViewModeToggle: true,
    showOwnerControls: true,

    // Styling
    headerHeight: 80,
    logoHeight: 40,
    position: 'sticky', // sticky|fixed|static
    layout: 'default', // default|centered|minimal|compact

    // Search Configuration
    searchPlaceholder: 'Search media...',
    searchPosition: 'center', // center|right|left|dropdown
    enableLiveSearch: true,
    showSearchOnMobile: false,

    // User Profile
    userProfileVisibility: 'auto', // auto|always|authenticated|never
    showProfileForPublic: false,
    requireLoginToView: false,

    // Advanced
    enableAPI: false,
    apiKey: '',
    enableWebhooks: false,
    customCSS: '',
    customJS: '',
    backupFrequency: 'weekly',
    cacheStrategy: 'browser'
    };
  });

  // Complete tab configuration
  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Building },
    { id: 'header', label: 'Header', icon: Layout },
    { id: 'management', label: 'Management', icon: Users },
    { id: 'design', label: 'Design', icon: Palette },
    { id: 'layout', label: 'Layout', icon: Layout },
    { id: 'booking', label: 'Booking System', icon: Calendar },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'interactions', label: 'Interactions', icon: MessageCircle },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'privacy', label: 'Privacy & Access', icon: Shield },
    { id: 'integrations', label: 'Integrations', icon: Zap },
    { id: 'seo', label: 'SEO & Domain', icon: Globe },
    { id: 'advanced', label: 'Advanced', icon: Code }
  ];

  useEffect(() => {
    if (book) {
      setFormData(prev => ({
        ...prev,
        ...book,
        organisationSettings: {
          ...prev.organisationSettings,
          ...book.organisationSettings
        },
        managementFeatures: {
          ...prev.managementFeatures,
          ...book.managementFeatures
        },

        headerSettings: {
          // Element Visibility
          showLogo: book.organisationSettings?.headerSettings?.showLogo ?? true,
          showTitle: book.organisationSettings?.headerSettings?.showTitle ?? true,
          showDescription: book.organisationSettings?.headerSettings?.showDescription ?? false,
          showSearch: book.organisationSettings?.headerSettings?.showSearch ?? true,
          showShareButton: book.organisationSettings?.headerSettings?.showShareButton ?? true,
          showAccessLevel: book.organisationSettings?.headerSettings?.showAccessLevel ?? true,
          showFilterControls: book.organisationSettings?.headerSettings?.showFilterControls ?? true,
          showGallerySelector: book.organisationSettings?.headerSettings?.showGallerySelector ?? true,
          showViewModeToggle: book.organisationSettings?.headerSettings?.showViewModeToggle ?? true,
          showOwnerControls: book.organisationSettings?.headerSettings?.showOwnerControls ?? true,

          // Styling
          headerHeight: book.organisationSettings?.headerSettings?.headerHeight || 80,
          logoHeight: book.organisationSettings?.headerSettings?.logoHeight || 40,
          position: book.organisationSettings?.headerSettings?.position || 'sticky',
          layout: book.organisationSettings?.headerSettings?.layout || 'default',

          // Search Configuration
          searchPlaceholder: book.organisationSettings?.headerSettings?.searchPlaceholder || 'Search media...',
          searchPosition: book.organisationSettings?.headerSettings?.searchPosition || 'center',
          enableLiveSearch: book.organisationSettings?.headerSettings?.enableLiveSearch ?? true,
          showSearchOnMobile: book.organisationSettings?.headerSettings?.showSearchOnMobile ?? false,

          // User Profile
          userProfileVisibility: book.organisationSettings?.headerSettings?.userProfileVisibility || 'auto',
          showProfileForPublic: book.organisationSettings?.headerSettings?.showProfileForPublic ?? false,
          requireLoginToView: book.organisationSettings?.headerSettings?.requireLoginToView ?? false,

          ...book.organisationSettings?.headerSettings
        },

        // Email notification settings
        emailNotifications: book?.settings?.emailNotifications || false,
        welcomeEmailsEnabled: book?.settings?.welcomeEmails || false,
        booking_confirmationEnabled: book?.settings?.bookingConfirmations || false,
        reminderEnabled: book?.settings?.reminderEmails || false,
        completion_certificateEnabled: book?.settings?.completionEmails || false,
        interaction_summaryEnabled: book?.settings?.interactionSummaries || false,
        
        // Custom messages
        welcomeMessage: book?.settings?.welcomeMessage || '',
        completionMessage: book?.settings?.completionMessage || '',
        
        // Notification frequencies
        userNotificationFreq: book?.settings?.userNotificationFreq || 'weekly',
        adminNotificationFreq: book?.settings?.adminNotificationFreq || 'daily'
      }));
    }
  }, [book]);

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      
      // Handle nested paths like 'bookingServices.appointment' or 'businessHours.monday.enabled'
      if (path.includes('.')) {
        const pathParts = path.split('.');
        let current = newData;
        
        // Navigate to the parent object
        for (let i = 0; i < pathParts.length - 1; i++) {
          const part = pathParts[i];
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
        
        // Set the final value
        current[pathParts[pathParts.length - 1]] = value;
      } else {
        // Simple top-level assignment
        newData[path] = value;
      }
      
      return newData;
    });
    
    setHasChanges(true);
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;
    
    setUploadStates(prev => ({ ...prev, [field]: 'uploading' }));
    
    try {
      const url = await firestoreService.uploadFile(bookId, field, file);
      handleInputChange(field, url);
      setUploadStates(prev => ({ ...prev, [field]: 'success' }));
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStates(prev => ({ ...prev, [field]: 'error' }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await firestoreService.updateBook(book.id, formData);
            onUpdate(formData);
           
      setHasChanges(false);
    } catch (error) {
      console.error('Save error:', error);
      setErrors({ general: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(formData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${formData.botName || 'organisation'}-settings.json`;
    link.click();
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        setFormData(prev => ({ ...prev, ...importedData }));
        setHasChanges(true);
      } catch (error) {
        setErrors({ general: 'Invalid file format. Please check the file and try again.' });
      }
    };
    reader.readAsText(file);
  };

  return (
    <SettingsModal
      title="Organisation Settings"
      subtitle="Configure your organisation's website and management features"
      isOpen={true}
      onClose={onClose}
      onSave={handleSave}
      onExport={handleExport}
      onImport={handleImport}
      saving={saving}
      headerColor="#4f46e5"
      hasChanges={hasChanges}
      size="max-w-7xl"
    >
      <div className="w-64 border-r border-gray-200 bg-gray-50 overflow-y-auto shrink-0">
        <div className="p-4">
            <TabNavigation 
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                hasChanges={hasChanges}
            />
            </div>
      </div>

        <div className="flex-1 overflow-y-auto min-h-0">
         <div className="p-6">
          {activeTab === 'basic' && (
            <BasicInfoTab
              formData={formData}
              bookId={bookId}
              onInputChange={handleInputChange}
              onImageUpload={handleImageUpload}
              templateType="organisation"
              uploadStates={uploadStates}
              errors={errors}
              organisationTypes={[
                { value: 'business', label: 'Business' },
                { value: 'church', label: 'Church' },
                { value: 'sports', label: 'Sports Club' },
                { value: 'union', label: 'Labour Union' },
                { value: 'political', label: 'Political Party' },
                { value: 'association', label: 'Association' }
              ]}
            />
          )}

          {activeTab === 'header' && (
            <HeaderSettings
              formData={formData}
              onInputChange={handleInputChange}
                          
              onImageUpload={handleImageUpload}
            />
          )}
          
          {activeTab === 'management' && (
            <ManagementSection
              formData={formData}
              onInputChange={handleInputChange}
              templateType="organisation"
            />
          )}
          
          {activeTab === 'design' && (
            <DesignBrandingSection
              formData={formData}
              onInputChange={handleInputChange}
              onImageUpload={handleImageUpload}
              uploadStates={uploadStates}
            />
          )}

          {activeTab === 'layout' && (
            <LayoutDisplaySection
              formData={formData}
              onInputChange={handleInputChange}
              templateType="organisation"
              availableSections={[
                { id: 'hero', label: 'Hero Section' },
                { id: 'about', label: 'About Us' },
                { id: 'leadership', label: 'Leadership Team' },
                { id: 'services', label: 'Services/Programs' },
                { id: 'events', label: 'Events' },
                { id: 'news', label: 'News & Updates' },
                { id: 'contact', label: 'Contact' }
              ]}
            />
          )}

          {activeTab === 'booking' && (
              <BookingSection
                formData={formData}
                onInputChange={handleInputChange}
              />
            )};
          
          {activeTab === 'interactions' && (
            <InteractionsSection
              formData={formData}
              onInputChange={handleInputChange}
              templateType="organisation"
            />
          )}

          {activeTab === 'payments' && (
            <PaymentsSection
              formData={formData}
              onInputChange={handleInputChange}
              templateType="organisation"
            />
          )}
          
          {activeTab === 'privacy' && (
            <PrivacySection
              formData={formData}
              onInputChange={handleInputChange}
              templateType="organisation"
            />
          )}

          {activeTab === 'email' && (
            <EmailSettingsSection
              formData={formData}
              onInputChange={handleInputChange}
              bookId={bookId}
              templateType={book?.botCategory}
              user={user}
            />
          )}
          
          {activeTab === 'integrations' && (
            <IntegrationsSection
              formData={formData}
              onInputChange={handleInputChange}
              templateType="organisation"
            />
          )}
          
          {activeTab === 'seo' && (
            <SEOSection
              formData={formData}
              onInputChange={handleInputChange}
              previewUrl={() => {
                if (formData.customDomain) {
                  return formData.customPath ? 
                    `https://${formData.customDomain}/${formData.customPath}` :
                    `https://${formData.customDomain}`;
                }
                return formData.customPath ? 
                  `https://app.parrotbook.com/${formData.customPath}` :
                  `https://app.parrotbook.com/book/${bookId}`;
              }}
            />
          )}
          
          {activeTab === 'advanced' && (
            <AdvancedSection
              formData={formData}
              onInputChange={handleInputChange}
              onExport={handleExport}
              onImport={handleImport}
              templateType="organisation"
            />
          )}
        </div>
      </div>
    </SettingsModal>
  );
};